import React, { useMemo } from 'react';
import { FaUser, FaBuilding, FaHome, FaMoneyBillWave, FaEdit, FaUserFriends, FaUserTie } from 'react-icons/fa';
import {
  StepContainer,
  SectionHeader,
  SectionTitle,
  SectionDivider,
  SectionDescription,
  ButtonContainer,
  PrimaryButton,
  SecondaryButton,
  ReviewCard,
  ReviewCardHeader,
  ReviewCardTitle,
  EditButton,
  ReviewRow,
  ReviewLabel,
  ReviewValue,
  TotalContainer,
  TotalLabel,
  TotalValue,
  LoadingSpinner,
} from '../../styles';
import { MobileAnalysisFormData } from '../../types';
import {
  propertyTypeOptions,
  validityOptions,
  maritalStatusOptions,
  employmentRelationshipOptions,
  genderOptions,
  politicallyExposedPersonOptions,
  nationalityOptions,
} from '../../constants';
import styled from 'styled-components';

const SubCard = styled.div`
  border-top: 1px solid #f0f0f0;
  margin-top: 10px;
  padding-top: 10px;
`;

const SubCardTitle = styled.p`
  font-size: 12px;
  font-weight: 600;
  color: #854EE0;
  margin: 0 0 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Badge = styled.span<{ variant?: 'purple' | 'green' | 'gray' }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background: ${props =>
    props.variant === 'purple' ? '#f3eeff' :
    props.variant === 'green' ? '#e8f5e9' : '#f5f5f5'};
  color: ${props =>
    props.variant === 'purple' ? '#854EE0' :
    props.variant === 'green' ? '#388e3c' : '#666'};
`;

interface Step5Props {
  formData: MobileAnalysisFormData;
  onSubmit: () => void;
  onBack: () => void;
  onEditStep: (step: number) => void;
  isSubmitting: boolean;
}

const Step5_Review: React.FC<Step5Props> = ({
  formData,
  onSubmit,
  onBack,
  onEditStep,
  isSubmitting,
}) => {
  const isNaturalPerson = formData.tenantType === 'NATURAL';
  const nt = formData.naturalTenant;
  const lt = formData.legalTenant;

  const parseMoneyValue = (value: string): number => {
    if (!value) return 0;
    return parseFloat(value.replace(/[R$\s.]/g, '').replace(',', '.')) || 0;
  };

  const totalAmount = useMemo(() => {
    const v = formData.values;
    let total = parseMoneyValue(v.rentValue);
    if (v.isCondominiumEnabled) total += parseMoneyValue(v.condominiumFee);
    if (v.isTaxesEnabled) total += parseMoneyValue(v.taxesValue);
    if (v.isEnergyEnabled) total += parseMoneyValue(v.energyBill);
    if (v.isWaterEnabled) total += parseMoneyValue(v.waterBill);
    if (v.isGasEnabled) total += parseMoneyValue(v.gasBill);
    return total;
  }, [formData.values]);

  const formatCurrency = (value: number): string =>
    value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const formatDate = (value: string): string => {
    if (!value) return '—';
    if (value.includes('-') && value.length === 10) {
      const [y, m, d] = value.split('-');
      return `${d}/${m}/${y}`;
    }
    return value;
  };

  const getLabel = (options: { value: string | number; text: string }[], value: string | number): string =>
    options.find((o) => o.value === value)?.text || String(value) || '—';

  const getPropertyTypeName = (v: string) => getLabel(propertyTypeOptions, v);
  const getValidityName = (v: number) => getLabel(validityOptions, v);
  const getMaritalStatusName = (v: string) => getLabel(maritalStatusOptions, v);
  const getEmploymentName = (v: string) => getLabel(employmentRelationshipOptions, v);
  const getGenderName = (v: string) => getLabel(genderOptions, v);
  const getPepLabel = (v: string) => getLabel(politicallyExposedPersonOptions, v);
  const getNationalityLabel = (v: string) => getLabel(nationalityOptions, v);

  const formatAddress = (): string => {
    const p = formData.property;
    let address = `${p.street}, ${p.number}`;
    if (p.complement) address += ` - ${p.complement}`;
    address += ` - ${p.district}, ${p.city}/${p.state}`;
    return address;
  };

  const Row = ({ label, value }: { label: string; value?: string | null }) =>
    value ? (
      <ReviewRow>
        <ReviewLabel>{label}</ReviewLabel>
        <ReviewValue>{value}</ReviewValue>
      </ReviewRow>
    ) : null;

  return (
    <StepContainer>
      <SectionHeader>
        <SectionTitle>Revisar Análise</SectionTitle>
        <SectionDivider />
      </SectionHeader>

      <SectionDescription>
        Confira os dados antes de enviar. Toque em "Editar" para fazer alterações.
      </SectionDescription>

      {/* ── Locatário / Empresa ── */}
      <ReviewCard>
        <ReviewCardHeader>
          <ReviewCardTitle>
            {isNaturalPerson ? <FaUser /> : <FaBuilding />}
            {isNaturalPerson ? 'Locatário' : 'Empresa'}
          </ReviewCardTitle>
          <EditButton type="button" onClick={() => onEditStep(2)}>
            <FaEdit size={12} /> Editar
          </EditButton>
        </ReviewCardHeader>

        {isNaturalPerson ? (
          <>
            {/* Dados pessoais */}
            <Row label="CPF" value={nt.cpf} />
            <Row label="Nome" value={nt.name} />
            {nt.hasSocialName && nt.socialName && (
              <Row label="Nome Social" value={nt.socialName} />
            )}
            <Row label="E-mail" value={nt.email} />
            <Row label="Telefone" value={nt.phone} />
            <Row label="Nascimento" value={formatDate(nt.birthDate)} />
            <Row label="Gênero" value={getGenderName(nt.gender)} />
            <Row label="Nome da Mãe" value={nt.motherName} />
            <Row label="Nacionalidade" value={getNationalityLabel(nt.nationality)} />
            <Row label="Estado Civil" value={getMaritalStatusName(nt.maritalStatus)} />

            {/* Informações profissionais */}
            <SubCard>
              <SubCardTitle>Informações Profissionais</SubCardTitle>
              <Row label="Vínculo" value={getEmploymentName(nt.employmentRelationship)} />
              {nt.employmentAdmissionDate && (
                <Row label="Admissão" value={formatDate(nt.employmentAdmissionDate)} />
              )}
              <Row label="Profissão" value={nt.profession} />
            </SubCard>

            {/* PEP */}
            {nt.politicallyExposedPerson !== 'NO' && (
              <SubCard>
                <SubCardTitle>Pessoa Politicamente Exposta</SubCardTitle>
                <ReviewRow>
                  <ReviewLabel>PEP</ReviewLabel>
                  <ReviewValue>
                    <Badge variant="purple">{getPepLabel(nt.politicallyExposedPerson)}</Badge>
                  </ReviewValue>
                </ReviewRow>
                {nt.politicallyExposedPerson === 'CLOSE_TO' && (
                  <>
                    <Row label="Nome PEP" value={nt.pepName} />
                    <Row label="CPF PEP" value={nt.pepCpf} />
                    <Row label="Parentesco" value={nt.pepKinship} />
                  </>
                )}
              </SubCard>
            )}

            {/* Cônjuge */}
            {['CASADO', 'UNIAO_ESTAVEL'].includes(nt.maritalStatus) && nt.partnerName && (
              <SubCard>
                <SubCardTitle>Cônjuge</SubCardTitle>
                <Row label="CPF" value={nt.partnerCpf} />
                <Row label="Nome" value={nt.partnerName} />
              </SubCard>
            )}

            {/* Residente */}
            <SubCard>
              <SubCardTitle>Residente no Imóvel</SubCardTitle>
              <ReviewRow>
                <ReviewLabel>Locatário é o residente?</ReviewLabel>
                <ReviewValue>
                  <Badge variant={nt.isResident ? 'green' : 'gray'}>
                    {nt.isResident ? 'Sim' : 'Não'}
                  </Badge>
                </ReviewValue>
              </ReviewRow>
              {!nt.isResident && (
                <>
                  <Row label="Nome" value={nt.residentName} />
                  <Row label="CPF" value={nt.residentCpf} />
                  <Row label="Nascimento" value={formatDate(nt.residentBirthDate)} />
                  <Row label="Gênero" value={getGenderName(nt.residentGender)} />
                  <Row label="Nome da Mãe" value={nt.residentMotherName} />
                  <Row label="E-mail" value={nt.residentEmail} />
                  <Row label="Telefone" value={nt.residentPhone} />
                  <Row label="Estado Civil" value={getMaritalStatusName(nt.residentMaritalStatus)} />
                  <Row label="Nacionalidade" value={getNationalityLabel(nt.residentNationality)} />
                  <Row label="Vínculo" value={getEmploymentName(nt.residentEmploymentRelationship)} />
                  <Row label="Profissão" value={nt.residentProfession} />
                  {nt.residentPoliticallyExposedPerson !== 'NO' && (
                    <>
                      <ReviewRow>
                        <ReviewLabel>PEP Residente</ReviewLabel>
                        <ReviewValue>
                          <Badge variant="purple">{getPepLabel(nt.residentPoliticallyExposedPerson)}</Badge>
                        </ReviewValue>
                      </ReviewRow>
                      {nt.residentPoliticallyExposedPerson === 'CLOSE_TO' && (
                        <>
                          <Row label="Nome PEP" value={nt.residentPepName} />
                          <Row label="CPF PEP" value={nt.residentPepCpf} />
                          <Row label="Parentesco" value={nt.residentPepKinship} />
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </SubCard>

            {/* Locatários Solidários */}
            {nt.includeSympathiseTenants && nt.sympathiseTenants.length > 0 && (
              <SubCard>
                <SubCardTitle>
                  Locatários Solidários ({nt.sympathiseTenants.length})
                </SubCardTitle>
                {nt.sympathiseTenants.map((t, i) => (
                  <div key={i} style={{ marginBottom: i < nt.sympathiseTenants.length - 1 ? 12 : 0 }}>
                    {i > 0 && <div style={{ borderTop: '1px dashed #e5e7eb', marginBottom: 10 }} />}
                    <ReviewRow>
                      <ReviewLabel style={{ fontWeight: 600 }}>#{i + 1} {t.name || '—'}</ReviewLabel>
                      <ReviewValue>{t.cpf}</ReviewValue>
                    </ReviewRow>
                    <Row label="Nascimento" value={formatDate(t.birthDate)} />
                    <Row label="Gênero" value={getGenderName(t.gender)} />
                    <Row label="Estado Civil" value={getMaritalStatusName(t.maritalStatus)} />
                    <Row label="Vínculo" value={getEmploymentName(t.employmentRelationship)} />
                    <Row label="Profissão" value={t.profession} />
                    {t.monthlyFixedIncome && (
                      <Row label="Renda Mensal" value={t.monthlyFixedIncome} />
                    )}
                    <ReviewRow>
                      <ReviewLabel>Residente</ReviewLabel>
                      <ReviewValue>
                        <Badge variant={t.resident ? 'green' : 'gray'}>{t.resident ? 'Sim' : 'Não'}</Badge>
                      </ReviewValue>
                    </ReviewRow>
                  </div>
                ))}
              </SubCard>
            )}
          </>
        ) : (
          <>
            {/* PJ */}
            <Row label="CNPJ" value={lt.cnpj} />
            <Row label="Razão Social" value={lt.companyName} />
            <Row label="E-mail" value={lt.email} />
            <Row label="Telefone" value={lt.phone} />
            <Row label="Constituição" value={formatDate(lt.foundationDate)} />
            {lt.lastContractualAlterationDate && (
              <Row label="Últ. Alt. Contratual" value={formatDate(lt.lastContractualAlterationDate)} />
            )}
            {lt.shareCapital && <Row label="Capital Social" value={lt.shareCapital} />}
            {lt.paidInCapital && <Row label="Capital Integralizado" value={lt.paidInCapital} />}
            {lt.anualRevenue && <Row label="Faturamento Anual" value={lt.anualRevenue} />}
            <Row label="Contato" value={lt.contactName} />
            {lt.contactPhone && <Row label="Tel. Contato" value={lt.contactPhone} />}

            {/* Sócios */}
            {lt.partners.length > 0 && lt.partners.some(p => p.name) && (
              <SubCard>
                <SubCardTitle>Sócios / Acionistas ({lt.partners.filter(p => p.name).length})</SubCardTitle>
                {lt.partners.filter(p => p.name).map((p, i) => (
                  <div key={i} style={{ marginBottom: i < lt.partners.length - 1 ? 10 : 0 }}>
                    {i > 0 && <div style={{ borderTop: '1px dashed #e5e7eb', marginBottom: 8 }} />}
                    <ReviewRow>
                      <ReviewLabel style={{ fontWeight: 600 }}>#{i + 1} {p.name}</ReviewLabel>
                      <ReviewValue>{p.cpf}</ReviewValue>
                    </ReviewRow>
                    {p.percentCapital && <Row label="% Capital" value={`${p.percentCapital}%`} />}
                    {p.dateOfEntry && <Row label="Entrada" value={formatDate(p.dateOfEntry)} />}
                  </div>
                ))}
              </SubCard>
            )}
          </>
        )}
      </ReviewCard>

      {/* ── Imóvel ── */}
      <ReviewCard>
        <ReviewCardHeader>
          <ReviewCardTitle>
            <FaHome />
            Imóvel
          </ReviewCardTitle>
          <EditButton type="button" onClick={() => onEditStep(3)}>
            <FaEdit size={12} /> Editar
          </EditButton>
        </ReviewCardHeader>

        <ReviewRow>
          <ReviewLabel>Endereço</ReviewLabel>
          <ReviewValue>{formatAddress()}</ReviewValue>
        </ReviewRow>
        <Row label="CEP" value={formData.property.zipCode} />
        <Row label="Tipo" value={getPropertyTypeName(formData.property.propertyType)} />
        <ReviewRow>
          <ReviewLabel>Finalidade</ReviewLabel>
          <ReviewValue>
            <Badge variant="purple">
              {formData.purpose === 'RESIDENTIAL' ? 'Residencial' : 'Comercial'}
            </Badge>
          </ReviewValue>
        </ReviewRow>
        <Row label="Motivo" value={formData.property.reason} />
      </ReviewCard>

      {/* ── Valores ── */}
      <ReviewCard>
        <ReviewCardHeader>
          <ReviewCardTitle>
            <FaMoneyBillWave />
            Valores
          </ReviewCardTitle>
          <EditButton type="button" onClick={() => onEditStep(4)}>
            <FaEdit size={12} /> Editar
          </EditButton>
        </ReviewCardHeader>

        <Row label="Aluguel" value={formData.values.rentValue || 'R$ 0,00'} />
        {formData.values.isCondominiumEnabled && (
          <Row label="Condomínio" value={formData.values.condominiumFee} />
        )}
        {formData.values.isTaxesEnabled && (
          <Row label="IPTU Mensal" value={formData.values.taxesValue} />
        )}
        {formData.values.isEnergyEnabled && (
          <Row label="Energia" value={formData.values.energyBill} />
        )}
        {formData.values.isWaterEnabled && (
          <Row label="Água" value={formData.values.waterBill} />
        )}
        {formData.values.isGasEnabled && (
          <Row label="Gás" value={formData.values.gasBill} />
        )}
        <Row label="Vigência" value={getValidityName(formData.values.validity)} />
      </ReviewCard>

      {/* ── Total ── */}
      <TotalContainer>
        <TotalLabel>Valor total da cobertura</TotalLabel>
        <TotalValue>{formatCurrency(totalAmount)}</TotalValue>
      </TotalContainer>

      <ButtonContainer>
        <SecondaryButton type="button" onClick={onBack} disabled={isSubmitting}>
          Voltar
        </SecondaryButton>
        <PrimaryButton type="button" onClick={onSubmit} disabled={isSubmitting} loading={isSubmitting}>
          {isSubmitting ? (
            <>
              <LoadingSpinner /> Enviando...
            </>
          ) : (
            'Enviar Análise'
          )}
        </PrimaryButton>
      </ButtonContainer>
    </StepContainer>
  );
};

export default Step5_Review;
